import React from 'react';
import { Instagram, Twitter, Linkedin, Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import { contactInfo } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-black pt-20 pb-8 overflow-hidden">
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <img
              src="https://customer-assets.emergentagent.com/job_faro-player-showcase/artifacts/6o32bteg_Logo_Blanco.png"
              alt="FARO Sports"
              className="h-12 w-auto mb-6"
            />
            <p className="text-white/50 max-w-md mb-8 leading-relaxed">
              Agencia de representación certificada por FIFA, creada para potenciar y promover el talento de jugadores de fútbol a nivel mundial.
            </p>
            <div className="flex gap-3">
              <a
                href={contactInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-white/5 hover:bg-white hover:text-black rounded-lg flex items-center justify-center text-white/60 transition-all duration-300"
              >
                <Instagram size={20} />
              </a>
              <a
                href={contactInfo.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-white/5 hover:bg-white hover:text-black rounded-lg flex items-center justify-center text-white/60 transition-all duration-300"
              >
                <Twitter size={20} />
              </a>
              <a
                href={contactInfo.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-white/5 hover:bg-white hover:text-black rounded-lg flex items-center justify-center text-white/60 transition-all duration-300"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-5">Enlaces</h4>
            <ul className="space-y-3">
              {['Nosotros', 'Servicios', 'Jugadores', 'Staff', 'Contacto'].map((link) => (
                <li key={link}>
                  <a
                    href={`#${link.toLowerCase()}`}
                    className="text-white/50 hover:text-white transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span>{link}</span>
                    <ArrowUpRight size={14} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-5">Contacto</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-white/50 hover:text-white transition-colors duration-300 flex items-center gap-3"
                >
                  <Mail size={18} />
                  <span className="text-sm">{contactInfo.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="text-white/50 hover:text-white transition-colors duration-300 flex items-center gap-3"
                >
                  <Phone size={18} />
                  <span className="text-sm">{contactInfo.phone}</span>
                </a>
              </li>
              <li>
                <span className="text-white/50 flex items-center gap-3">
                  <MapPin size={18} />
                  <span className="text-sm">{contactInfo.address}</span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/10 mb-8" />

        {/* Partners Section */}
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-8 py-4">
          <a
            href="https://www.fifa.com/football-agents"
            target="_blank"
            rel="noopener noreferrer"
            className="relative group"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/partners/fifa_agent.png`}
              alt="FIFA Agent"
              className="h-8 opacity-50 group-hover:opacity-80 transition-opacity duration-300"
            />
            <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white text-black text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
              Agente FIFA Certificado
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45" />
            </span>
          </a>
          <div className="w-px h-6 bg-white/20" />
          <a
            href="https://www.nike.com"
            target="_blank"
            rel="noopener noreferrer"
            className="relative group"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/partners/nike.png`}
              alt="Nike Partner"
              className="h-6 opacity-50 group-hover:opacity-80 transition-opacity duration-300"
            />
            <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white text-black text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
              Convenio Nike
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45" />
            </span>
          </a>
          <div className="w-px h-6 bg-white/20" />
          <a
            href="https://sportcore.artificialmente.uy/admin/about"
            target="_blank"
            rel="noopener noreferrer"
            className="relative group"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/partners/sportcore.png`}
              alt="SportCore"
              className="h-8 opacity-50 group-hover:opacity-80 transition-opacity duration-300"
            />
            <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white text-black text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
              Plataforma profesional de gestión de jugadores
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45" />
            </span>
          </a>
          <div className="w-px h-6 bg-white/20" />
          <a
            href="https://artificialmente.uy"
            target="_blank"
            rel="noopener noreferrer"
            className="relative group"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/partners/artificialmente.png`}
              alt="Artificialmente"
              className="h-6 opacity-50 group-hover:opacity-80 transition-opacity duration-300"
            />
            <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white text-black text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
              Desarrollo Artificialmente - Inteligencia Artificial
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45" />
            </span>
          </a>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/10 mb-8" />

        {/* Bottom Section */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © {currentYear} FARO Sports. Todos los derechos reservados.
          </p>
          <p className="text-white/30 text-xs">
            Desarrollado por{' '}
            <a href="https://artificialmente.uy" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors">
              Artificialmente
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
